/* AcademyAdminPanel — embedded inside /admin under the "Academy" tab.
 * Auth: uses the current admin password to obtain a JWT cookie session
 * (admin@zhelta.com is seeded with the same password as ADMIN_PASSWORD).
 * Once authed, all /api/academy/admin/* calls work via cookies. */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Users, Plus, Trash2, BookOpen, Folder, Upload, X, GraduationCap, RefreshCw,
  KeyRound, Pencil, Layers, History, FileSpreadsheet, Image as ImageIcon, Mail, Award, FileText,
} from 'lucide-react';
import LessonsTab from '../components/admin/LessonsTab';
import CertificatesTab from '../components/admin/CertificatesTab';
import RecordsTab from '../components/admin/RecordsTab';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const api = axios.create({ baseURL: API, withCredentials: true });

const ROLES = ['student', 'parent', 'instructor', 'admin'];

function Section({ title, icon: Icon, action, children }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E5E7] overflow-hidden mb-6">
      <div className="bg-[#0e0e10] text-white px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#f5a623]" />}
          <span className="text-[13px] font-bold tracking-[0.18em] uppercase">{title}</span>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function AcademyAdminPanel({ adminPassword }) {
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [sub, setSub] = useState('users'); // users | enrollments | documents | content
  const [users, setUsers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // user being password-reset
  const [refreshKey, setRefreshKey] = useState(0);

  // Establish a JWT session. Try the existing cookie first (set by the
  // division login portal at /zhelta-internal/{systems|services|academy}).
  // If that fails AND we have the legacy admin password, fall back to
  // logging in as admin@zhelta.com — the original Super-Admin path.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.get('/api/auth/me');
        if (!cancelled) { setAuthed(true); setAuthError(''); }
        return;
      } catch { /* fall through to password fallback */ }
      if (!adminPassword) {
        if (!cancelled) setAuthError('Sign in via /zhelta-internal/console or enter the admin password.');
        return;
      }
      try {
        await api.post('/api/auth/login', { email: 'admin@zhelta.com', password: adminPassword });
        if (!cancelled) { setAuthed(true); setAuthError(''); }
      } catch (err) {
        if (!cancelled) {
          setAuthed(false);
          setAuthError(err.response?.data?.detail || err.message || 'Login failed');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [adminPassword]);

  // Load reference data once authed
  useEffect(() => {
    if (!authed) return;
    api.get('/api/academy/programs').then(({ data }) => {
      setPrograms(data.programs || []);
      setTiers(data.tiers || []);
    });
  }, [authed]);

  const reload = useCallback(async () => {
    if (!authed) return;
    const [u, e] = await Promise.all([
      api.get('/api/academy/admin/users'),
      api.get('/api/academy/admin/enrollments'),
    ]);
    setUsers(u.data.rows || []);
    setEnrollments(e.data.rows || []);
  }, [authed]);

  useEffect(() => { reload(); }, [reload, refreshKey]);

  if (!authed && authError) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E5E7] p-6">
        <div className="text-[#6E6E73] text-[14px]">{authError}</div>
      </div>
    );
  }
  if (!authed) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E5E7] p-6">
        <div className="text-[#6E6E73] text-[14px]">Authenticating academy admin session…</div>
      </div>
    );
  }

  const SubTab = ({ id, label, icon: Icon }) => (
    <button
      type="button"
      onClick={() => setSub(id)}
      data-testid={`acad-subtab-${id}`}
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-bold tracking-[0.16em] uppercase transition-colors ${
        sub === id ? 'bg-[#c9a227] text-[#0e0e10]' : 'bg-white border border-[#E5E5E7] text-[#1D1D1F] hover:border-[#c9a227]'
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <SubTab id="users" label="Users" icon={Users} />
        <SubTab id="enrollments" label="Enrollments" icon={GraduationCap} />
        <SubTab id="cohorts" label="Cohorts" icon={Layers} />
        <SubTab id="programs" label="Programs" icon={Layers} />
        <SubTab id="discounts" label="Discounts" icon={KeyRound} />
        <SubTab id="documents" label="Documents" icon={Folder} />
        <SubTab id="modules" label="Modules & Assignments" icon={BookOpen } />
        <SubTab id="lessons" label="Course Content" icon={BookOpen} />
        <SubTab id="certificates" label="Certificates" icon={Award} />
        <SubTab id="records" label="Records" icon={FileText} />
        <SubTab id="demo" label="Demo accounts" icon={KeyRound} />
        <SubTab id="audit" label="Audit log" icon={History} />
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold text-[#6E6E73] hover:text-[#c9a227]"
          data-testid="academy-admin-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {sub === 'users' && (
        <UsersTab
          users={users} programs={programs}
          onCreate={() => setShowCreate(true)}
          onChange={reload}
          onReset={(u) => setResetTarget(u)}
        />
      )}
      {sub === 'enrollments' && (
        <EnrollmentsTab
          users={users} enrollments={enrollments}
          programs={programs} tiers={tiers}
          onChange={reload}
        />
      )}
      {sub === 'cohorts' && (
        <CohortsTab />
      )}
      {sub === 'programs' && (
        <ProgramsTab onChange={reload} programs={programs} tiers={tiers} />
      )}
      {sub === 'discounts' && (
        <DiscountsTab programs={programs} />
      )}
      {sub === 'documents' && (
        <DocumentsTab users={users} />
      )}
      {sub === 'modules' && (
        <ContentTab programs={programs} />
      )}
      {sub === 'lessons' && (
        <LessonsTab programs={programs} />
      )}
      {sub === 'certificates' && (
        <CertificatesTab programs={programs} />
      )}
      {sub === 'records' && (
        <RecordsTab />
      )}
      {sub === 'demo' && (
        <DemoAccountsTab />
      )}
      {sub === 'audit' && (
        <AuditTab />
      )}

      {showCreate && <CreateUserModal programs={programs} onClose={() => setShowCreate(false)} onCreated={reload} />}
      {resetTarget && <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} />}
    </div>
  );
}

/* ======================================== USERS ======================================== */

function UsersTab({ users, onCreate, onChange, onReset }) {
  const [filter, setFilter] = useState('');
  const [role, setRole] = useState('');
  const filtered = users.filter((u) => {
    if (role && u.role !== role) return false;
    if (filter && !`${u.email} ${u.name}`.toLowerCase().includes(filter.toLowerCase())) return false;
    return true;
  });

  const remove = async (id) => {
    if (!window.confirm('Delete this user? Their enrollments and documents will also be removed.')) return;
    try {
      await api.delete(`/api/academy/admin/users/${id}`);
      onChange();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  return (
    <Section
      title={`Users (${filtered.length})`} icon={Users}
      action={
        <button onClick={onCreate} data-testid="academy-admin-create-user"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]"
          style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
          <Plus className="w-3.5 h-3.5" /> New user
        </button>
      }
    >
      <div className="flex flex-wrap gap-2 mb-4">
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search name or email"
          data-testid="academy-admin-user-search"
          className="px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13px] w-64" />
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="academy-admin-user-role-filter"
          className="px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13px]">
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-[#FAFAFA] text-[#6E6E73]">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Name</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Email</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Role</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Linked</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E7]">
            {filtered.length === 0 ? (
              <tr><td colSpan="5" className="px-3 py-6 text-center text-[#9ca3af]">No users.</td></tr>
            ) : filtered.map((u, idx) => (
              <tr key={u.id || `${u.email}-${idx}`} data-testid={`academy-admin-user-${u.id || idx}`}>
                <td className="px-3 py-2 font-bold text-[#1D1D1F]">{u.name}</td>
                <td className="px-3 py-2 text-[#6E6E73]">{u.email}</td>
                <td className="px-3 py-2"><span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-[#a8871a] border border-amber-200">{u.role}</span></td>
                <td className="px-3 py-2 text-[#6E6E73]">{(u.linked_student_emails || []).join(', ') || '—'}</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => onReset(u)} data-testid={`academy-admin-user-reset-${u.id || u.email}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-[#a8871a] hover:bg-amber-50 border border-transparent hover:border-amber-200">
                      <KeyRound className="w-3 h-3" /> Reset
                    </button>
                    <button onClick={() => remove(u.id)} data-testid={`academy-admin-user-delete-${u.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function CreateUserModal({ programs, onClose, onCreated }) {
  const [form, setForm] = useState({ email: '', name: '', role: 'student', password: '', phone: '', linked_student_emails: '' });
  const [enroll, setEnroll] = useState({ program_slug: '', tier_id: 'standard', schedule: '' });
  const [tempPassword, setTempPassword] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const body = { ...form };
      if (!body.password) delete body.password;
      if (!body.phone) delete body.phone;
      if (form.role === 'parent' && form.linked_student_emails.trim()) {
        body.linked_student_emails = form.linked_student_emails.split(',').map((s) => s.trim()).filter(Boolean);
      } else {
        delete body.linked_student_emails;
      }
      const { data } = await api.post('/api/academy/admin/users', body);
      setTempPassword(data.temp_password);

      // If we also asked to enroll, do that now
      if (form.role === 'student' && enroll.program_slug) {
        await api.post('/api/academy/admin/enrollments', {
          user_id: data.id,
          program_slug: enroll.program_slug,
          tier_id: enroll.tier_id,
          schedule: enroll.schedule || undefined,
        });
      }
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-start justify-center pt-16 px-4">
      <div className="w-full max-w-[520px] bg-white rounded-2xl shadow-2xl">
        <div className="px-6 py-4 border-b border-[#E5E5E7] flex items-center justify-between">
          <h3 className="text-[16px] font-extrabold text-[#1D1D1F]">{tempPassword ? 'User created' : 'Create new user'}</h3>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5 text-[#6E6E73]" /></button>
        </div>
        {tempPassword ? (
          <div className="p-6 space-y-3">
            <p className="text-[14px] text-[#6E6E73]">Account created and credentials emailed.</p>
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-[13px]">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#a8871a] mb-1">Temporary password</div>
              <code className="font-bold text-[#1D1D1F]">{tempPassword}</code>
            </div>
            <button onClick={onClose} data-testid="academy-admin-create-done"
              className="w-full px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10]"
              style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3">
            <Field label="Full name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="acad-create-name" />
            <Field label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="acad-create-email" />
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1">Role</label>
              <select required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="acad-create-role"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <Field label="Password (auto-generated if empty, min 8 chars)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="acad-create-password" />
            <Field label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="acad-create-phone" />
            {form.role === 'parent' && (
              <Field label="Linked student emails (comma-separated)" value={form.linked_student_emails} onChange={(v) => setForm({ ...form, linked_student_emails: v })} testId="acad-create-linked" />
            )}
            {form.role === 'student' && (
              <div className="rounded-lg border border-[#E5E5E7] p-3 space-y-2 bg-[#FAFAFA]">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#a8871a]">Optional: also enroll</div>
                <select value={enroll.program_slug} onChange={(e) => setEnroll({ ...enroll, program_slug: e.target.value })}
                  data-testid="acad-create-enroll-program"
                  className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] outline-none text-[13.5px]">
                  <option value="">— No enrollment —</option>
                  {programs.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                </select>
                <Field label="Schedule (e.g. Mon/Wed 6pm GMT)" value={enroll.schedule} onChange={(v) => setEnroll({ ...enroll, schedule: v })} testId="acad-create-enroll-schedule" />
              </div>
            )}
            {error && <div className="text-red-600 text-[13px]">{error}</div>}
            <button type="submit" disabled={loading} data-testid="acad-create-submit"
              className="w-full px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
              style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
              {loading ? 'Creating…' : 'Create user'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
    </div>
  );
}

/* ======================================== ENROLLMENTS ======================================== */

function EnrollmentsTab({ users, enrollments, programs, tiers, onChange }) {
  const [form, setForm] = useState({ user_id: '', program_slug: '', tier_id: 'standard', schedule: '', instructor_id: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showM365Bulk, setShowM365Bulk] = useState(false);
  const students = users.filter((u) => u.role === 'student');
  const instructors = users.filter((u) => u.role === 'instructor');

  const enroll = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await api.post('/api/academy/admin/enrollments', {
        user_id: form.user_id, program_slug: form.program_slug, tier_id: form.tier_id,
        schedule: form.schedule || undefined,
        instructor_id: form.instructor_id || undefined,
      });
      setForm({ user_id: '', program_slug: '', tier_id: 'standard', schedule: '', instructor_id: '' });
      onChange();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (user_id, program_slug) => {
    if (!window.confirm('Remove this enrollment?')) return;
    await api.delete(`/api/academy/admin/enrollments?user_id=${user_id}&program_slug=${program_slug}`);
    onChange();
  };

  const onResendOnboarding = async (user_id) => {
    if (!window.confirm('Rotate the temp password and resend the welcome email? The student will receive a fresh login link.')) return;
    try {
      const { data } = await api.post(`/api/academy/admin/users/${user_id}/resend-onboarding`);
      alert(`Welcome email resent to ${data.email}. The status pill will update to "Sent" and flip to "Delivered" once Resend confirms.`);
      onChange();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const onOneShot = async (e) => {
    const u = userMap[e.user_id];
    const pctRaw = window.prompt(`One-shot discount for ${u?.name || u?.email || 'this student'} on "${e.program_name}" (${e.tier_id}).\n\nEnter % off (1–90):`, '15');
    if (pctRaw === null) return;
    const pct = Number(String(pctRaw).trim());
    if (!Number.isFinite(pct) || pct < 1 || pct > 90) {
      alert('Please enter a number between 1 and 90.');
      return;
    }
    try {
      const params = new URLSearchParams({
        program_slug: e.program_slug, tier_id: e.tier_id || 'standard',
        percent: String(Math.round(pct)),
      });
      if (u?.email) params.set('student_email', u.email);
      const { data } = await api.post(`/api/academy/admin/discounts/one-shot?${params.toString()}`);
      const link = data.payment_link || `${window.location.origin}/academy/enroll?slug=${e.program_slug}&tier=${e.tier_id || 'standard'}&promo=${data.code}&via=admin`;
      try { await navigator.clipboard.writeText(link); } catch {/* ignore */}
      alert(`✅ Code ${data.code} created (${pct}% off, single use, expires in 7 days).\n\nLink copied to clipboard:\n${link}`);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const [m365Target, setM365Target] = useState(null);
  const [m365Map, setM365Map] = useState({}); // {`${user_id}::${program_slug}`: row}
  useEffect(() => {
    api.get('/api/academy/admin/m365-access').then(({ data }) => {
      const map = {};
      (data.rows || []).forEach((r) => { map[`${r.user_id}::${r.program_slug}`] = r; });
      setM365Map(map);
    }).catch(() => {});
  }, [enrollments]);

  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));

  return (
    <>
      <Section title="Enroll a student" icon={Plus}
        action={
          <div className="inline-flex items-center gap-2">
            <button type="button" onClick={() => setShowM365Bulk(true)} data-testid="academy-admin-bulk-m365"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] border border-[#0e0e10] hover:bg-[#0e0e10] hover:text-white transition-colors">
              <Mail className="w-3.5 h-3.5" /> Bulk M365 (CSV)
            </button>
            <button type="button" onClick={() => setShowBulk(true)} data-testid="academy-admin-bulk-enroll"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]"
              style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
              <FileSpreadsheet className="w-3.5 h-3.5" /> Bulk import (CSV)
            </button>
          </div>
        }
      >
        <form onSubmit={enroll} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <select required value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}
            data-testid="acad-enroll-student"
            className="px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]">
            <option value="">Select student…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.email})</option>)}
          </select>
          <select required value={form.program_slug} onChange={(e) => setForm({ ...form, program_slug: e.target.value })}
            data-testid="acad-enroll-program"
            className="px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]">
            <option value="">Select program…</option>
            {programs.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
          </select>
          <select value={form.tier_id} onChange={(e) => setForm({ ...form, tier_id: e.target.value })}
            data-testid="acad-enroll-tier"
            className="px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]">
            {tiers.map((t) => <option key={t.id} value={t.id}>{t.name} — ${t.price}</option>)}
          </select>
          <select value={form.instructor_id} onChange={(e) => setForm({ ...form, instructor_id: e.target.value })}
            data-testid="acad-enroll-instructor"
            className="px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]">
            <option value="">No instructor</option>
            {instructors.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.email})</option>)}
          </select>
          <input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })}
            placeholder="Schedule (e.g. Mon/Wed 6pm GMT)"
            data-testid="acad-enroll-schedule"
            className="md:col-span-2 px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
          {error && <div className="md:col-span-2 text-red-600 text-[13px]">{error}</div>}
          <button type="submit" disabled={loading} data-testid="acad-enroll-submit"
            className="md:col-span-2 px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
            {loading ? 'Enrolling…' : 'Enroll'}
          </button>
        </form>
      </Section>

      <Section title={`All enrollments (${enrollments.length})`} icon={GraduationCap}>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[#FAFAFA] text-[#6E6E73]">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase">Student</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase">Program</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase">Tier</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase">Schedule</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase">Progress</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase">Onboarding email</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E7]">
              {enrollments.length === 0 ? (
                <tr><td colSpan="7" className="px-3 py-6 text-center text-[#9ca3af]">No enrollments yet.</td></tr>
              ) : enrollments.map((e) => {
                const u = userMap[e.user_id];
                return (
                  <tr key={`${e.user_id}-${e.program_slug}`}>
                    <td className="px-3 py-2 font-bold text-[#1D1D1F]">{u?.name || '?'} <span className="text-[#6E6E73] font-normal">({u?.email || '?'})</span></td>
                    <td className="px-3 py-2">{e.program_name}</td>
                    <td className="px-3 py-2 text-[#6E6E73]">{e.tier_id}</td>
                    <td className="px-3 py-2 text-[#6E6E73]">{e.schedule || '—'}</td>
                    <td className="px-3 py-2">{e.progress || 0}%{e.grade ? ` · ${e.grade}` : ''}</td>
                    <td className="px-3 py-2"><EmailStatusPill ev={e.onboarding_email} /></td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => onOneShot(e)} data-testid={`acad-oneshot-${e.user_id}-${e.program_slug}`}
                          title="Mint a single-use discount code (7 days, this program/tier only) and copy the payment link"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold text-[#0e0e10] hover:brightness-105"
                          style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
                          🎁 1-shot
                        </button>
                        <M365Cell grant={m365Map[`${e.user_id}::${e.program_slug}`]}
                          onClick={() => setM365Target({ user_id: e.user_id, program_slug: e.program_slug, tier_id: e.tier_id, user: u, existing: m365Map[`${e.user_id}::${e.program_slug}`] })} />
                        {(() => {
                          const st = e.onboarding_email?.status;
                          if (!st || st === 'bounced' || st === 'not-sent' || st === 'complained') {
                            return (
                              <button onClick={() => onResendOnboarding(e.user_id)}
                                data-testid={`acad-resend-onboarding-${e.user_id}-${e.program_slug}`}
                                title="Rotate temp password & resend the welcome email"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold text-[#a8871a] bg-amber-50 hover:bg-amber-100">
                                <Mail className="w-3 h-3" /> Resend
                              </button>
                            );
                          }
                          return null;
                        })()}
                        <button onClick={() => remove(e.user_id, e.program_slug)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-red-600 hover:bg-red-50">
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {showBulk && <BulkEnrollModal onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); onChange(); }} />}
      {showM365Bulk && <BulkM365Modal onClose={() => setShowM365Bulk(false)} onDone={() => {
        setShowM365Bulk(false);
        api.get('/api/academy/admin/m365-access').then(({ data }) => {
          const map = {};
          (data.rows || []).forEach((r) => { map[`${r.user_id}::${r.program_slug}`] = r; });
          setM365Map(map);
        }).catch(() => {});
      }} />}
      {m365Target && (
        <M365GrantModal
          target={m365Target}
          onClose={() => setM365Target(null)}
          onSaved={() => {
            setM365Target(null);
            // refresh m365 grants
            api.get('/api/academy/admin/m365-access').then(({ data }) => {
              const map = {};
              (data.rows || []).forEach((r) => { map[`${r.user_id}::${r.program_slug}`] = r; });
              setM365Map(map);
            }).catch(() => {});
          }}
        />
      )}
    </>
  );
}

/* ======================================== M365 ACCESS ======================================== */

function M365Cell({ grant, onClick }) {
  if (grant) {
    return (
      <button onClick={onClick} data-testid={`m365-edit-${grant.user_id}-${grant.program_slug}`}
        title={`M365: ${grant.m365_email} · ${grant.apps.join(', ')}`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200">
        M365 ✓ ({grant.apps.length})
      </button>
    );
  }
  return (
    <button onClick={onClick} data-testid="m365-grant-btn"
      title="Provision Microsoft 365 access for this enrollment"
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold text-[#a8871a] bg-[#fff8e7] hover:bg-amber-100 border border-amber-200">
      M365…
    </button>
  );
}

const M365_APPS = ['outlook', 'word', 'excel', 'powerpoint', 'onedrive', 'teams'];

function M365GrantModal({ target, onClose, onSaved }) {
  const existing = target.existing;
  const [m365Email, setM365Email] = useState(existing?.m365_email || '');
  const [apps, setApps] = useState(new Set(existing?.apps || ['outlook', 'word', 'excel', 'onedrive']));
  const [notes, setNotes] = useState(existing?.notes || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState({});
  const [tierBonuses, setTierBonuses] = useState({});

  // Pull the per-program templates so the "Use template" button knows what to tick.
  useEffect(() => {
    api.get('/api/academy/admin/m365-access').then(({ data }) => {
      setTemplates(data.templates || {});
      setTierBonuses(data.tier_bonuses || {});
    }).catch(() => {});
  }, []);

  // Pre-apply the template for new grants (don't override existing manual choices).
  useEffect(() => {
    if (existing) return;
    const base = templates[target.program_slug];
    if (!base) return;
    const next = new Set(base);
    const tier = target.tier_id;
    if (tier && tierBonuses[tier]) {
      tierBonuses[tier].forEach((a) => next.add(a));
    }
    setApps(next);
  }, [templates, tierBonuses, target.program_slug, target.tier_id, existing]);

  const applyTemplate = () => {
    const base = templates[target.program_slug] || ['outlook', 'word', 'onedrive', 'teams'];
    const next = new Set(base);
    const tier = target.tier_id;
    if (tier && tierBonuses[tier]) {
      tierBonuses[tier].forEach((a) => next.add(a));
    }
    setApps(next);
  };

  const toggle = (a) => {
    const n = new Set(apps);
    n.has(a) ? n.delete(a) : n.add(a);
    setApps(n);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api.post('/api/academy/admin/m365-access', {
        user_id: target.user_id,
        program_slug: target.program_slug,
        m365_email: m365Email.trim(),
        apps: Array.from(apps),
        notes: notes.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm('Revoke M365 access for this enrollment? The student will lose the launch tiles on their dashboard.')) return;
    setBusy(true);
    try {
      await api.delete(`/api/academy/admin/m365-access?user_id=${target.user_id}&program_slug=${target.program_slug}`);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-[520px] bg-white rounded-2xl overflow-hidden border border-[#E5E5E7]">
        <div className="bg-[#0e0e10] text-white px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#f5a623]">Microsoft 365 access</div>
            <div className="text-[14px] font-extrabold">{target.user?.name || target.user?.email} · {target.program_slug}</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1.5">Student M365 email</label>
            <input type="email" required value={m365Email}
              onChange={(e) => setM365Email(e.target.value)}
              data-testid="m365-modal-email"
              placeholder="jane.doe@yourtenant.onmicrosoft.com"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
            <p className="text-[11px] text-[#6E6E73] mt-1">Create the user in Microsoft Admin Center first, then paste their UPN here.</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73]">Apps assigned</label>
              {templates[target.program_slug] && (
                <button type="button" onClick={applyTemplate}
                  data-testid="m365-modal-use-template"
                  title={`Default for ${target.program_slug}${target.tier_id ? ' · ' + target.tier_id + ' tier' : ''}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-wider text-[#0e0e10]"
                  style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
                  ⚡ Use template
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {M365_APPS.map((a) => (
                <label key={a} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${apps.has(a) ? 'border-[#c9a227] bg-[#fff8e7]' : 'border-[#E5E5E7] hover:border-[#c9a227]/60'}`}>
                  <input type="checkbox" checked={apps.has(a)} onChange={() => toggle(a)}
                    data-testid={`m365-modal-app-${a}`}
                    className="accent-[#c9a227]" />
                  <span className="text-[13px] font-bold capitalize">{a}</span>
                </label>
              ))}
            </div>
            {templates[target.program_slug] && (
              <p className="text-[11px] text-[#6E6E73] mt-1.5">
                Template for <strong>{target.program_slug}</strong>: {templates[target.program_slug].join(', ')}
                {target.tier_id && tierBonuses[target.tier_id] && (
                  <> · <strong>{target.tier_id}</strong> tier adds: {tierBonuses[target.tier_id].join(', ')}</>
                )}
              </p>
            )}
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1.5">Internal notes (optional)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="m365-modal-notes"
              placeholder="License SKU, expiry, etc."
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
          </div>
          {error && <div className="text-red-600 text-[12.5px]">{error}</div>}
          <div className="flex items-center justify-between gap-2 pt-1">
            <div>
              {existing && (
                <button type="button" onClick={revoke}
                  className="px-3 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-red-600 hover:bg-red-50">
                  Revoke
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose}
                className="px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#6E6E73] hover:text-[#1D1D1F]">Cancel</button>
              <button type="submit" disabled={busy} data-testid="m365-modal-save"
                className="px-5 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
                style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
                {busy ? 'Saving…' : (existing ? 'Update access' : 'Grant access')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ======================================== DOCUMENTS ======================================== */

function DocumentsTab({ users }) {
  const [userId, setUserId] = useState('');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const upload = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const fd = new FormData();
      fd.append('user_id', userId);
      fd.append('title', title);
      fd.append('file', file);
      await api.post('/api/academy/admin/documents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(`Uploaded "${title}".`);
      setTitle(''); setFile(null);
      // reset file input
      const input = document.getElementById('acad-doc-file-input');
      if (input) input.value = '';
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section title="Upload a document for a user" icon={Upload}>
      <form onSubmit={upload} className="space-y-3 max-w-[520px]">
        <select required value={userId} onChange={(e) => setUserId(e.target.value)}
          data-testid="acad-doc-user"
          className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]">
          <option value="">Select recipient…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email}) — {u.role}</option>)}
        </select>
        <Field label="Document title" required value={title} onChange={setTitle} testId="acad-doc-title" />
        <input id="acad-doc-file-input" type="file" required onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="acad-doc-file"
          className="block w-full text-[13px] text-[#6E6E73] file:mr-3 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-[#fef8e7] file:text-[#a8871a] file:font-bold file:cursor-pointer" />
        {error && <div className="text-red-600 text-[13px]">{error}</div>}
        {success && <div className="text-emerald-600 text-[13px]">{success}</div>}
        <button type="submit" disabled={loading || !file} data-testid="acad-doc-submit"
          className="px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
          style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
          {loading ? 'Uploading…' : 'Upload'}
        </button>
      </form>
      <p className="text-[12px] text-[#6E6E73] mt-3">Files are stored in MongoDB (max 10MB). Recipients see them under "Document Management" in their portal.</p>
    </Section>
  );
}

/* ======================================== CONTENT (Modules + Assignments) ======================================== */

function ContentTab({ programs }) {
  const [slug, setSlug] = useState(programs[0]?.slug || '');
  const [modules, setModules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showModule, setShowModule] = useState(false);
  const [showAssignment, setShowAssignment] = useState(false);

  useEffect(() => {
    if (!programs.length) return;
    if (!slug) setSlug(programs[0].slug);
  }, [programs, slug]);

  const reload = useCallback(async () => {
    if (!slug) return;
    const [m, a] = await Promise.all([
      api.get(`/api/academy/admin/modules?program_slug=${slug}`),
      api.get(`/api/academy/admin/assignments?program_slug=${slug}`),
    ]);
    setModules(m.data.rows || []);
    setAssignments(a.data.rows || []);
  }, [slug]);

  useEffect(() => { reload(); }, [reload]);

  const removeMod = async (id) => {
    if (!window.confirm('Delete module?')) return;
    await api.delete(`/api/academy/admin/modules/${id}`);
    reload();
  };
  const removeAsn = async (id) => {
    if (!window.confirm('Delete assignment? Submissions will also be removed.')) return;
    await api.delete(`/api/academy/admin/assignments/${id}`);
    reload();
  };

  return (
    <>
      <Section title="Course content" icon={BookOpen}>
        <div className="mb-4">
          <select value={slug} onChange={(e) => setSlug(e.target.value)}
            data-testid="acad-content-program"
            className="px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]">
            {programs.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px] font-bold tracking-[0.18em] uppercase text-[#6E6E73]">Modules ({modules.length})</span>
              <button onClick={() => setShowModule(true)} data-testid="acad-content-new-module"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-[#0e0e10]"
                style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
                <Plus className="w-3 h-3" /> New
              </button>
            </div>
            <ul className="space-y-1.5">
              {modules.length === 0 ? <li className="text-[12.5px] text-[#9ca3af]">No modules yet.</li> :
                modules.map((m) => (
                  <li key={m.id} className="flex items-start gap-2 px-3 py-2 rounded-lg border border-[#E5E5E7]">
                    <span className="text-[10px] font-bold tracking-wider text-[#a8871a] pt-0.5">#{m.order}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[13.5px] text-[#1D1D1F] truncate">{m.title}</div>
                      {m.video_url && <div className="text-[11px] text-[#6E6E73] truncate">{m.video_url}</div>}
                    </div>
                    <button onClick={() => removeMod(m.id)} className="text-red-600 hover:bg-red-50 rounded p-1">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </li>
                ))}
            </ul>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px] font-bold tracking-[0.18em] uppercase text-[#6E6E73]">Assignments ({assignments.length})</span>
              <button onClick={() => setShowAssignment(true)} data-testid="acad-content-new-assignment"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-[#0e0e10]"
                style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
                <Plus className="w-3 h-3" /> New
              </button>
            </div>
            <ul className="space-y-1.5">
              {assignments.length === 0 ? <li className="text-[12.5px] text-[#9ca3af]">No assignments yet.</li> :
                assignments.map((a) => (
                  <li key={a.id} className="flex items-start gap-2 px-3 py-2 rounded-lg border border-[#E5E5E7]">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[13.5px] text-[#1D1D1F] truncate">{a.title}</div>
                      <div className="text-[11px] text-[#6E6E73]">{a.due_at ? `Due ${a.due_at.slice(0, 10)}` : 'No due date'} · {a.max_points} pts</div>
                    </div>
                    <button onClick={() => removeAsn(a.id)} className="text-red-600 hover:bg-red-50 rounded p-1">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </Section>

      {showModule && <ModuleModal slug={slug} onClose={() => setShowModule(false)} onSaved={() => { setShowModule(false); reload(); }} />}
      {showAssignment && <AssignmentModal slug={slug} onClose={() => setShowAssignment(false)} onSaved={() => { setShowAssignment(false); reload(); }} />}
    </>
  );
}

function ModuleModal({ slug, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', body_md: '', video_url: '', order: 0 });
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/academy/admin/modules', { program_slug: slug, ...form, order: Number(form.order) || 0 });
      onSaved();
    } catch (err) { setError(err.response?.data?.detail || err.message); }
  };
  return <ModalShell title="New module" onClose={onClose}>
    <form onSubmit={submit} className="space-y-3">
      <Field label="Title" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="acad-module-title" />
      <Field label="Order (1, 2, 3…)" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} testId="acad-module-order" />
      <Field label="Video URL (optional)" value={form.video_url} onChange={(v) => setForm({ ...form, video_url: v })} testId="acad-module-video" />
      <div>
        <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1">Body (Markdown)</label>
        <textarea rows={5} value={form.body_md} onChange={(e) => setForm({ ...form, body_md: e.target.value })}
          data-testid="acad-module-body"
          className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px] font-mono" />
      </div>
      {error && <div className="text-red-600 text-[13px]">{error}</div>}
      <button type="submit" data-testid="acad-module-submit"
        className="w-full px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10]"
        style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>Save module</button>
    </form>
  </ModalShell>;
}

function AssignmentModal({ slug, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', body: '', due_at: '', max_points: 100 });
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { program_slug: slug, ...form, max_points: Number(form.max_points) || 100 };
      if (!body.due_at) delete body.due_at;
      await api.post('/api/academy/admin/assignments', body);
      onSaved();
    } catch (err) { setError(err.response?.data?.detail || err.message); }
  };
  return <ModalShell title="New assignment" onClose={onClose}>
    <form onSubmit={submit} className="space-y-3">
      <Field label="Title" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="acad-asn-title" />
      <Field label="Due date (YYYY-MM-DD, optional)" type="date" value={form.due_at} onChange={(v) => setForm({ ...form, due_at: v })} testId="acad-asn-due" />
      <Field label="Max points" type="number" value={form.max_points} onChange={(v) => setForm({ ...form, max_points: v })} testId="acad-asn-points" />
      <div>
        <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1">Description</label>
        <textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
          data-testid="acad-asn-body"
          className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
      </div>
      {error && <div className="text-red-600 text-[13px]">{error}</div>}
      <button type="submit" data-testid="acad-asn-submit"
        className="w-full px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10]"
        style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>Save assignment</button>
    </form>
  </ModalShell>;
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-start justify-center pt-16 px-4">
      <div className="w-full max-w-[520px] bg-white rounded-2xl shadow-2xl">
        <div className="px-6 py-4 border-b border-[#E5E5E7] flex items-center justify-between">
          <h3 className="text-[16px] font-extrabold text-[#1D1D1F]">{title}</h3>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5 text-[#6E6E73]" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

/* ======================================== RESET PASSWORD ======================================== */

function ResetPasswordModal({ user, onClose }) {
  const [tempPwd, setTempPwd] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const reset = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.post(`/api/academy/admin/users/${user.id}/reset-password`);
      setTempPwd(data.temp_password);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(tempPwd);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <ModalShell title={tempPwd ? 'Password reset' : `Reset password for ${user.name || user.email}`} onClose={onClose}>
      {!tempPwd ? (
        <div className="space-y-4">
          <p className="text-[14px] text-[#6E6E73]">
            This will generate a fresh temporary password, save it to <strong>{user.email}</strong>, email them the new credentials, and clear any active brute-force lockout.
          </p>
          {error && <div className="text-red-600 text-[13px]">{error}</div>}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-full text-[12.5px] font-bold uppercase tracking-wider text-[#6E6E73] border border-[#E5E5E7]">Cancel</button>
            <button onClick={reset} disabled={loading} data-testid="academy-admin-reset-confirm"
              className="flex-1 px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
              style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
              {loading ? 'Resetting…' : 'Reset password'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[14px] text-[#6E6E73]">
            Done. We've emailed the new password to <strong>{user.email}</strong>. You can also copy it from below if you need to share it directly.
          </p>
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-center justify-between gap-3">
            <code className="font-bold text-[#1D1D1F] text-[15px] break-all">{tempPwd}</code>
            <button onClick={copy} data-testid="academy-admin-reset-copy"
              className="shrink-0 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-[#a8871a] bg-white border border-amber-200">
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <button onClick={onClose} data-testid="academy-admin-reset-done"
            className="w-full px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10]"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>Done</button>
        </div>
      )}
    </ModalShell>
  );
}

/* ======================================== PROGRAMS (custom CRUD) ======================================== */

function ProgramsTab({ onChange, programs = [], tiers = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/academy/admin/programs');
      setRows(data.rows || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const remove = async (slug) => {
    if (!window.confirm(`Delete program "${slug}"? Existing student enrollments will be kept but the program won't be visible in the catalog.`)) return;
    try {
      await api.delete(`/api/academy/admin/programs/${slug}`);
      reload();
      onChange && onChange();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  return (
    <>
      <ShareLinkWidget programs={programs} tiers={tiers} />
      <Section title="Custom programs" icon={Layers}
      action={
        <button onClick={() => setShowCreate(true)} data-testid="academy-admin-create-program"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]"
          style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
          <Plus className="w-3.5 h-3.5" /> New program
        </button>
      }>
      {loading ? <div className="text-[#6E6E73] text-[13px]">Loading…</div> : rows.length === 0 ? (
        <div className="text-[13.5px] text-[#6E6E73] py-3">
          No custom programs yet. The 11 built-in programs are always available; add your own here and they'll show up alongside them on <code className="text-[12px]">/academy/programs</code> and in enrollment dropdowns.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[#FAFAFA] text-[#6E6E73]">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Name</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Slug</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Duration</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Level</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E7]">
              {rows.map((p) => (
                <tr key={p.slug} data-testid={`academy-admin-program-${p.slug}`}>
                  <td className="px-3 py-2 font-bold text-[#1D1D1F]">{p.name}</td>
                  <td className="px-3 py-2 text-[#6E6E73]"><code className="text-[12px]">{p.slug}</code></td>
                  <td className="px-3 py-2 text-[#6E6E73]">{p.duration || '—'}</td>
                  <td className="px-3 py-2 text-[#6E6E73]">{p.level || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => setEditTarget(p)}
                        data-testid={`acad-prog-edit-${p.slug}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-[#a8871a] hover:bg-amber-50">
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      <button onClick={() => remove(p.slug)}
                        data-testid={`acad-prog-delete-${p.slug}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-red-600 hover:bg-red-50">
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showCreate && <ProgramFormModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); reload(); onChange && onChange(); }} />}
      {editTarget && <ProgramFormModal program={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); reload(); onChange && onChange(); }} />}
    </Section>
    </>
  );
}

/* ============================== DEMO ACCOUNTS ============================== */

function DemoAccountsTab() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null); // { role, ...account }
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/academy/admin/demo-accounts');
        setAccounts(data.accounts || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1600);
    } catch {/* ignore */}
  };

  return (
    <Section title="Demo accounts" icon={KeyRound}>
      <p className="text-[13px] text-[#6E6E73] mb-4">
        Shared sandbox credentials — admin-only. Use them yourself for live walk-throughs, or click <strong>Email creds</strong> to send the access details to a specific student, parent or tutor (e.g. as their post-payment testing background or before a demo call). They&apos;re never exposed on the public site.
      </p>
      {loading ? <div className="text-[13px] text-[#6E6E73]">Loading…</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {accounts.map((a) => (
            <div key={a.role} data-testid={`demo-account-${a.role}`}
              className="rounded-2xl border border-[#E5E5E7] bg-white p-5">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#a8871a]">{a.role}</div>
                  <div className="text-[15px] font-extrabold text-[#1D1D1F]">{a.label}</div>
                </div>
              </div>
              <div className="text-[12px] text-[#6E6E73] leading-snug mb-3">{a.desc}</div>
              <div className="space-y-1.5">
                <button onClick={() => copy(`${a.role}::email`, a.email)}
                  data-testid={`demo-${a.role}-copy-email`}
                  className="w-full inline-flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md bg-[#FAFAFA] border border-[#E5E5E7] hover:border-[#c9a227]/60 transition-colors text-left">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#6E6E73]">Email</span>
                  <code className="text-[11.5px] font-mono text-[#1D1D1F] flex-1 truncate text-right">{a.email}</code>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#a8871a]">{copiedKey === `${a.role}::email` ? '✓' : 'Copy'}</span>
                </button>
                <button onClick={() => copy(`${a.role}::password`, a.password)}
                  data-testid={`demo-${a.role}-copy-password`}
                  className="w-full inline-flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md bg-[#FAFAFA] border border-[#E5E5E7] hover:border-[#c9a227]/60 transition-colors text-left">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#6E6E73]">Password</span>
                  <code className="text-[11.5px] font-mono text-[#1D1D1F] flex-1 truncate text-right">{a.password}</code>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#a8871a]">{copiedKey === `${a.role}::password` ? '✓' : 'Copy'}</span>
                </button>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => setTarget(a)}
                  data-testid={`demo-${a.role}-email-creds`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[11.5px] font-bold uppercase tracking-wider text-[#0e0e10]"
                  style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
                  Email creds
                </button>
                <a href={`/academy/login?portal=${a.role}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-3 py-2 rounded-full text-[11.5px] font-bold uppercase tracking-wider border border-[#0e0e10] text-[#0e0e10] hover:bg-[#0e0e10] hover:text-white transition-colors"
                  title="Open the login page in a new tab">
                  Open
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
      {target && <SendDemoCredsModal account={target} onClose={() => setTarget(null)} />}
    </Section>
  );
}

function SendDemoCredsModal({ account, onClose }) {
  const [form, setForm] = useState({ to_email: '', to_name: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api.post('/api/academy/admin/send-demo-credentials', {
        role: account.role,
        to_email: form.to_email.trim(),
        to_name: form.to_name.trim() || null,
        note: form.note.trim() || null,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Send failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-[460px] bg-white rounded-2xl overflow-hidden border border-[#E5E5E7]">
        <div className="bg-[#0e0e10] text-white px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#f5a623]">Send {account.role} demo creds</div>
            <div className="text-[14px] font-extrabold">{account.label}</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="send-demo-close"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="p-6 text-center">
            <div className="text-[14px] font-bold text-emerald-700 mb-2">Email sent!</div>
            <div className="text-[12.5px] text-[#6E6E73] mb-4">The {account.label} credentials were emailed to <strong>{form.to_email}</strong>.</div>
            <button onClick={onClose} className="px-5 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]"
              style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1.5">Recipient email</label>
              <input type="email" required value={form.to_email}
                onChange={(e) => setForm({ ...form, to_email: e.target.value })}
                data-testid="send-demo-email"
                placeholder="parent@example.com"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1.5">Recipient name (optional)</label>
              <input value={form.to_name}
                onChange={(e) => setForm({ ...form, to_name: e.target.value })}
                data-testid="send-demo-name"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1.5">Personal note (optional)</label>
              <textarea rows={3} value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="send-demo-note"
                placeholder="Hi, here are your sandbox credentials so you can explore your portal before our session…"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
            </div>
            {error && <div className="text-red-600 text-[12.5px]">{error}</div>}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#6E6E73] hover:text-[#1D1D1F]">Cancel</button>
              <button type="submit" disabled={busy} data-testid="send-demo-submit"
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
                style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
                {busy ? 'Sending…' : 'Send credentials'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ============================== ENROLLMENT LINK GENERATOR ============================== */

function DiscountsTab({ programs = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const reload = () => {
    setLoading(true);
    api.get('/api/academy/admin/discounts').then(({ data }) => setRows(data.rows || []))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const toggle = async (code, active) => {
    await api.patch(`/api/academy/admin/discounts/${code}?active=${active}`);
    reload();
  };
  const remove = async (code) => {
    if (!window.confirm(`Permanently delete code "${code}"? Existing checkouts that already used it are unaffected.`)) return;
    await api.delete(`/api/academy/admin/discounts/${code}`);
    reload();
  };
  const copyLink = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {/* ignore */}
  };

  return (
    <Section title="Discount codes" icon={KeyRound}
      action={
        <button onClick={() => setShowCreate(true)} data-testid="discount-create-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]"
          style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
          <Plus className="w-3.5 h-3.5" /> New code
        </button>
      }>
      <p className="text-[13px] text-[#6E6E73] mb-4">
        Create promotional codes to share with students or parents who ask for a discount. Codes are validated at Stripe checkout — students can paste them on the enrollment page or you can append <code>&amp;promo=CODE</code> to the share link.
      </p>
      {loading ? <div className="text-[#6E6E73] text-[13px]">Loading…</div> : (
        <div className="overflow-x-auto rounded-xl border border-[#E5E5E7]">
          <table className="w-full text-[13px]">
            <thead className="bg-[#FAFAFA] text-[#6E6E73]">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase">Code</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase">% off</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase">Used</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase">Expires</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase">Programs</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase">State</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E7]">
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-[#6E6E73]">No discount codes yet — create one to share with students who ask.</td></tr>
              ) : rows.map((d) => (
                <tr key={d.code} data-testid={`discount-row-${d.code}`}>
                  <td className="px-3 py-2 font-mono font-bold text-[#1D1D1F]">{d.code}</td>
                  <td className="px-3 py-2 font-extrabold text-[#a8871a]">{d.percent}%</td>
                  <td className="px-3 py-2 text-[#6E6E73]">{d.used_count || 0}{d.max_uses ? ` / ${d.max_uses}` : ''}</td>
                  <td className="px-3 py-2 text-[#6E6E73]">{d.expires_at ? new Date(d.expires_at).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2 text-[#6E6E73] text-[11.5px]">{d.program_slugs?.length ? `${d.program_slugs.length} program${d.program_slugs.length === 1 ? '' : 's'}` : 'All'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${d.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                      {d.active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => copyLink(d.code)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-[#a8871a] bg-amber-50 hover:bg-amber-100 mr-1">Copy</button>
                    <button onClick={() => toggle(d.code, !d.active)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-[#1D1D1F] bg-gray-50 hover:bg-gray-100 mr-1">
                      {d.active ? 'Disable' : 'Enable'}
                    </button>
                    <button onClick={() => remove(d.code)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showCreate && <CreateDiscountModal programs={programs} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); reload(); }} />}
    </Section>
  );
}

function CreateDiscountModal({ programs, onClose, onSaved }) {
  const [form, setForm] = useState({ code: '', percent: 10, max_uses: '', expires_at: '', program_slugs: [], note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const togglePgm = (slug) => {
    const cur = new Set(form.program_slugs);
    cur.has(slug) ? cur.delete(slug) : cur.add(slug);
    setForm({ ...form, program_slugs: Array.from(cur) });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api.post('/api/academy/admin/discounts', {
        code: form.code.trim(),
        percent: Number(form.percent),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at || null,
        program_slugs: form.program_slugs.length ? form.program_slugs : null,
        note: form.note || null,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="New discount code" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1.5">Code</label>
            <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              data-testid="discount-modal-code"
              placeholder="EARLYBIRD15" pattern="[A-Z0-9_-]+" maxLength={40}
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px] font-mono uppercase" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1.5">Percent off</label>
            <input type="number" required min={1} max={90} value={form.percent}
              onChange={(e) => setForm({ ...form, percent: e.target.value })}
              data-testid="discount-modal-percent"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1.5">Max uses (blank = unlimited)</label>
            <input type="number" min={1} value={form.max_uses}
              onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
              data-testid="discount-modal-max-uses"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1.5">Expires (blank = never)</label>
            <input type="date" value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
              data-testid="discount-modal-expires"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1.5">
            Limit to programs (none selected = all)
          </label>
          <div className="grid grid-cols-2 gap-1.5 max-h-[180px] overflow-y-auto pr-2">
            {programs.map((p) => (
              <label key={p.slug} className={`inline-flex items-center gap-2 px-2 py-1.5 rounded-md border cursor-pointer text-[12px] ${form.program_slugs.includes(p.slug) ? 'border-[#c9a227] bg-[#fff8e7]' : 'border-[#E5E5E7]'}`}>
                <input type="checkbox" checked={form.program_slugs.includes(p.slug)} onChange={() => togglePgm(p.slug)} className="accent-[#c9a227]" />
                <span className="truncate">{p.name}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1.5">Internal note (optional)</label>
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="e.g. for parent of John Doe — referrals"
            className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
        </div>
        {error && <div className="text-red-600 text-[13px]">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#6E6E73] hover:text-[#1D1D1F]">Cancel</button>
          <button type="submit" disabled={busy} data-testid="discount-modal-save"
            className="px-5 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
            {busy ? 'Creating…' : 'Create code'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function CohortsTab() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const reload = () => {
    setError('');
    api.get('/api/academy/admin/cohorts').then(({ data }) => setRows(data.rows || []))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  };
  useEffect(reload, []);

  if (rows === null) {
    return <Section title="Cohort dashboard" icon={Layers}><div className="text-[#6E6E73] text-[13px]">Loading…</div></Section>;
  }
  if (rows.length === 0) {
    return <Section title="Cohort dashboard" icon={Layers}><div className="text-[#6E6E73] text-[13px]">No cohorts yet — once students enroll, you'll see one card per program × tier here.</div></Section>;
  }

  // Aggregate roll-up across all cohorts
  const total = rows.reduce((a, r) => a + r.students, 0);
  const sumOnboard = rows.reduce((a, r) => a + Math.round(r.onboarded_pct * r.students / 100), 0);
  const sumM365 = rows.reduce((a, r) => a + Math.round(r.m365_pct * r.students / 100), 0);
  const avgProg = total ? Math.round(rows.reduce((a, r) => a + r.avg_progress * r.students, 0) / total) : 0;

  return (
    <Section title="Cohort dashboard" icon={Layers}
      action={
        <button onClick={reload} data-testid="cohorts-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] border border-[#0e0e10] hover:bg-[#0e0e10] hover:text-white transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      }>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        <Stat label="Total cohorts" value={rows.length} />
        <Stat label="Total students" value={total} tone="blue" />
        <Stat label="Avg progress" value={`${avgProg}%`} tone="emerald" />
        <Stat label="Onboarded" value={`${total ? Math.round(100 * sumOnboard / total) : 0}%`} tone="emerald" />
      </div>
      {error && <div className="text-red-600 text-[13px] mb-3">{error}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {rows.map((r) => (
          <CohortCard key={`${r.program_slug}::${r.tier_id}`} c={r} />
        ))}
      </div>
      <div className="mt-3 text-[11.5px] text-[#6E6E73]">
        Roll-up across all cohorts: {sumM365}/{total} students have M365 access · {sumOnboard}/{total} confirmed onboarding email delivery.
      </div>
    </Section>
  );
}

function CohortCard({ c }) {
  // Simple traffic-light health based on the weakest dimension.
  const health = (() => {
    if (c.students === 0) return 'gray';
    const score = Math.min(c.onboarded_pct, c.m365_pct, c.avg_progress + 30);
    if (c.bounced_pct > 20) return 'red';
    if (score >= 70) return 'green';
    if (score >= 40) return 'amber';
    return 'red';
  })();
  const ring = {
    green: 'border-emerald-200 bg-emerald-50/30',
    amber: 'border-amber-200 bg-amber-50/30',
    red:   'border-red-200 bg-red-50/30',
    gray:  'border-[#E5E5E7] bg-white',
  }[health];
  return (
    <div data-testid={`cohort-card-${c.program_slug}-${c.tier_id}`}
      className={`rounded-2xl border ${ring} p-4`}>
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#a8871a]">{c.tier_id} tier</div>
          <div className="text-[15px] font-extrabold text-[#1D1D1F] truncate">{c.program_name}</div>
        </div>
        <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border
          ${health === 'green' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
            health === 'amber' ? 'bg-amber-100 text-amber-800 border-amber-200' :
            health === 'red'   ? 'bg-red-100 text-red-800 border-red-200' :
            'bg-gray-100 text-gray-600 border-gray-200'}`}>
          {health === 'green' ? '✓ Healthy' : health === 'amber' ? '! Watch' : health === 'red' ? '✕ At risk' : '— Empty'}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3 text-[12px]">
        <Metric label="Students" value={c.students} />
        <Metric label="Avg progress" value={`${c.avg_progress}%`} />
        <Metric label="Onboarded" value={`${c.onboarded_pct}%`} bar={c.onboarded_pct} barColor="bg-emerald-400" />
        <Metric label="M365 ready" value={`${c.m365_pct}%`} bar={c.m365_pct} barColor="bg-sky-400" />
      </div>
      {c.bounced_pct > 0 && (
        <div className="mt-2 text-[11px] text-red-600 font-bold">⚠ {c.bounced_pct}% bounced/spam — check Resend webhook.</div>
      )}
      <div className="mt-3 pt-2 border-t border-[#E5E5E7] text-[10.5px] text-[#9ca3af] flex items-center justify-between">
        <span>First: {c.first_enrolled ? new Date(c.first_enrolled).toLocaleDateString() : '—'}</span>
        <span>Last: {c.last_enrolled ? new Date(c.last_enrolled).toLocaleDateString() : '—'}</span>
      </div>
    </div>
  );
}

function Metric({ label, value, bar, barColor = 'bg-[#c9a227]' }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10.5px] uppercase tracking-wider font-bold text-[#6E6E73]">
        <span>{label}</span>
        <span className="text-[#1D1D1F] text-[12.5px] tracking-normal normal-case">{value}</span>
      </div>
      {typeof bar === 'number' && (
        <div className="mt-1 h-1.5 rounded-full bg-[#E5E5E7] overflow-hidden">
          <div className={`h-full ${barColor}`} style={{ width: `${Math.max(0, Math.min(100, bar))}%` }} />
        </div>
      )}
    </div>
  );
}

const EMAIL_STATUS_STYLES = {
  delivered:  { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', label: 'Delivered' },
  opened:     { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', label: 'Opened' },
  clicked:    { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', label: 'Clicked' },
  sent:       { bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200',   label: 'Sent' },
  delivery_delayed: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200',  label: 'Delayed' },
  bounced:    { bg: 'bg-red-50',      text: 'text-red-700',     border: 'border-red-200',     label: 'Bounced' },
  complained: { bg: 'bg-red-50',      text: 'text-red-700',     border: 'border-red-200',     label: 'Spam' },
  'not-sent': { bg: 'bg-gray-50',     text: 'text-gray-500',    border: 'border-gray-200',    label: 'Not sent' },
};

function EmailStatusPill({ ev }) {
  if (!ev) return <span className="text-[11px] text-[#9ca3af]">—</span>;
  const s = EMAIL_STATUS_STYLES[ev.status] || EMAIL_STATUS_STYLES['not-sent'];
  const ts = ev.status_at || ev.sent_at;
  const tsLabel = ts ? new Date(ts).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '';
  return (
    <span
      data-testid={`email-status-${ev.recipient}`}
      title={`${s.label}${tsLabel ? ' · ' + tsLabel : ''}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border ${s.bg} ${s.text} ${s.border}`}
    >
      {s.label}
    </span>
  );
}

function ShareLinkWidget({ programs = [], tiers = [] }) {
  const [copiedKey, setCopiedKey] = useState(null);
  const [filter, setFilter] = useState('');

  const filtered = filter
    ? programs.filter((p) => p.name.toLowerCase().includes(filter.toLowerCase()) || p.slug.toLowerCase().includes(filter.toLowerCase()))
    : programs;

  const linkFor = (slug, tierId) =>
    `${window.location.origin}/academy/enroll?slug=${encodeURIComponent(slug)}&tier=${encodeURIComponent(tierId)}&via=admin`;

  const priceFor = (program, tierId) => program?.prices?.[tierId] ?? null;

  const copy = async (slug, tierId) => {
    const link = linkFor(slug, tierId);
    try {
      await navigator.clipboard.writeText(link);
      setCopiedKey(`${slug}::${tierId}`);
      setTimeout(() => setCopiedKey(null), 1800);
    } catch {/* ignore */}
  };

  return (
    <Section title="Generate enrollment payment link" icon={KeyRound}>
      <p className="text-[13px] text-[#6E6E73] mb-4">
        Pricing &amp; payment are kept private from public visitors. Pick any program × tier below and copy the personal payment link to email or text the student or parent. Each link unlocks a Stripe checkout pre-filled for that program. Append a discount code to the URL (e.g. <code>&amp;promo=SUMMER15</code>) or manage codes from the Discounts tab.
      </p>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter programs…"
          data-testid="share-link-filter"
          className="px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px] w-full sm:w-64"
        />
        <span className="text-[12px] text-[#6E6E73]">{filtered.length} of {programs.length} program{programs.length === 1 ? '' : 's'}</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-[#E5E5E7]">
        <table className="w-full text-[13px]">
          <thead className="bg-[#FAFAFA] text-[#6E6E73]">
            <tr>
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Program</th>
              {tiers.map((t) => (
                <th key={t.id} className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">
                  {t.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E7] bg-white">
            {filtered.length === 0 ? (
              <tr><td colSpan={tiers.length + 1} className="px-3 py-6 text-center text-[#6E6E73]">No programs match.</td></tr>
            ) : filtered.map((p) => (
              <tr key={p.slug} data-testid={`share-link-row-${p.slug}`}>
                <td className="px-3 py-2.5 align-top">
                  <div className="font-bold text-[#1D1D1F]">{p.name}</div>
                  <code className="text-[11px] text-[#6E6E73]">{p.slug}</code>
                </td>
                {tiers.map((t) => {
                  const key = `${p.slug}::${t.id}`;
                  const isCopied = copiedKey === key;
                  const price = priceFor(p, t.id);
                  return (
                    <td key={t.id} className="px-3 py-2.5 align-top whitespace-nowrap">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <div className="text-[14px] font-extrabold text-[#a8871a]">
                            {price != null ? `$${price}` : '—'}
                          </div>
                          <div className="inline-flex items-center gap-1 mt-1">
                            <button
                              type="button"
                              onClick={() => copy(p.slug, t.id)}
                              data-testid={`share-link-copy-${p.slug}-${t.id}`}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors ${
                                isCopied
                                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                  : 'text-[#0e0e10] hover:brightness-105'
                              }`}
                              style={isCopied ? undefined : { background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}
                            >
                              {isCopied ? 'Copied!' : 'Copy'}
                            </button>
                            <a
                              href={linkFor(p.slug, t.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`share-link-preview-${p.slug}-${t.id}`}
                              title="Preview the recipient experience"
                              className="inline-flex items-center px-2 py-1 rounded-md text-[11px] border border-[#E5E5E7] text-[#6E6E73] hover:border-[#c9a227] hover:text-[#a8871a]"
                            >
                              Preview
                            </a>
                          </div>
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[11.5px] text-[#6E6E73]">
        Tip: built-in programs are always available; new programs you add below appear here automatically.
      </p>
    </Section>
  );
}

function ProgramFormModal({ program, onClose, onSaved }) {
  const isEdit = !!program;
  const [form, setForm] = useState({
    slug: program?.slug || '',
    name: program?.name || '',
    summary: program?.summary || '',
    description: program?.description || '',
    tagline: program?.tagline || '',
    image_url: program?.image_url || '',
    duration: program?.duration || '',
    level: program?.level || '',
    audience: program?.audience || '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const body = { ...form };
      Object.keys(body).forEach((k) => { if (body[k] === '' && k !== 'name' && k !== 'slug') delete body[k]; });
      let savedSlug = program?.slug;
      if (isEdit) {
        delete body.slug;
        await api.patch(`/api/academy/admin/programs/${program.slug}`, body);
      } else {
        const res = await api.post('/api/academy/admin/programs', body);
        savedSlug = res.data.slug;
      }
      // If admin attached an image, upload it now (after the program exists)
      if (imageFile && savedSlug) {
        setUploading(true);
        const fd = new FormData();
        fd.append('file', imageFile);
        await api.post(`/api/academy/admin/programs/${savedSlug}/image`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploading(false);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  return (
    <ModalShell title={isEdit ? `Edit "${program.name}"` : 'Create new program'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        {!isEdit && (
          <Field label="URL slug (kebab-case, e.g. linux-administration)" required value={form.slug}
            onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') })}
            testId="acad-prog-slug" />
        )}
        <Field label="Program name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="acad-prog-name" />
        <Field label="Tagline (one short line shown in hero)" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testId="acad-prog-tagline" />
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1">Summary (shown on the program card)</label>
          <textarea rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
            data-testid="acad-prog-summary"
            className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1">Full description (Markdown OK)</label>
          <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="acad-prog-description"
            className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
        </div>

        {/* Hero image — upload-and-attach */}
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#6E6E73] mb-1">Hero image</label>
          <div className="flex items-center gap-3">
            {(imageFile || form.image_url) && (
              <div className="w-20 h-20 rounded-lg overflow-hidden border border-[#E5E5E7] bg-[#FAFAFA] shrink-0">
                {imageFile ? (
                  <img alt="preview" src={URL.createObjectURL(imageFile)} className="w-full h-full object-cover" />
                ) : (
                  <img alt="current" src={form.image_url} className="w-full h-full object-cover" />
                )}
              </div>
            )}
            <label className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-[#E5E5E7] cursor-pointer hover:border-[#c9a227] text-[12.5px] text-[#6E6E73]">
              <ImageIcon className="w-4 h-4 text-[#c9a227]" />
              {imageFile ? imageFile.name : 'Choose an image (JPG/PNG, max 4MB)'}
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                data-testid="acad-prog-image-file" />
            </label>
          </div>
          <p className="text-[11px] text-[#9ca3af] mt-1">
            {isEdit ? 'Leave empty to keep the current image.' : 'Upload happens right after the program is created.'}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Duration (e.g. 10 weeks)" value={form.duration} onChange={(v) => setForm({ ...form, duration: v })} testId="acad-prog-duration" />
          <Field label="Level" value={form.level} onChange={(v) => setForm({ ...form, level: v })} testId="acad-prog-level" />
          <Field label="Audience" value={form.audience} onChange={(v) => setForm({ ...form, audience: v })} testId="acad-prog-audience" />
        </div>
        {error && <div className="text-red-600 text-[13px]">{error}</div>}
        <button type="submit" disabled={loading || uploading || !form.name || (!isEdit && !form.slug)}
          data-testid="acad-prog-submit"
          className="w-full px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
          style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
          {uploading ? 'Uploading image…' : loading ? 'Saving…' : (isEdit ? 'Save changes' : 'Create program (with starter modules)')}
        </button>
        {!isEdit && (
          <p className="text-[11.5px] text-[#6E6E73]">
            Creating a program also seeds 3 starter modules + 1 reflection assignment so students enrolling immediately see content.
          </p>
        )}
      </form>
    </ModalShell>
  );
}

/* ======================================== AUDIT LOG ======================================== */

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [actionChip, setActionChip] = useState(''); // '' | 'access' | 'password' | 'lesson' | 'enrollment'

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/academy/admin/audit?limit=200`);
      setRows(data.rows || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const ACTION_GROUPS = {
    access:     ['user.revoke_access', 'user.restore_access', 'admin.sign_out_everywhere', 'admin.emergency_sign_out'],
    password:   ['user.password_reset', 'onboarding.resend'],
    lesson:     ['lesson.approve', 'lesson.reject'],
    enrollment: ['enrollment.bulk_import', 'user.create', 'user.delete'],
  };

  const filtered = rows.filter((r) => {
    if (actionChip && !(ACTION_GROUPS[actionChip] || []).includes(r.action)) return false;
    if (!filter) return true;
    const hay = `${r.action} ${r.target} ${r.actor_email}`.toLowerCase();
    return hay.includes(filter.toLowerCase());
  });

  const ACTION_BADGE = {
    'user.create':           'bg-emerald-50 text-emerald-700 border-emerald-200',
    'user.delete':           'bg-red-50 text-red-700 border-red-200',
    'user.password_reset':   'bg-amber-50 text-amber-700 border-amber-200',
    'user.revoke_access':    'bg-red-100 text-red-800 border-red-300',
    'user.restore_access':   'bg-emerald-100 text-emerald-800 border-emerald-300',
    'admin.sign_out_everywhere': 'bg-slate-100 text-slate-800 border-slate-300',
    'admin.emergency_sign_out':  'bg-rose-100 text-rose-800 border-rose-300',
    'program.create':        'bg-emerald-50 text-emerald-700 border-emerald-200',
    'program.update':        'bg-blue-50 text-blue-700 border-blue-200',
    'program.delete':        'bg-red-50 text-red-700 border-red-200',
    'program.image_upload':  'bg-indigo-50 text-indigo-700 border-indigo-200',
    'enrollment.bulk_import':'bg-sky-50 text-sky-700 border-sky-200',
  };

  // True when the audit row's `target` looks like an email (user-related row)
  // — we only deep-link those into the Users tab.
  const isEmailTarget = (s) => typeof s === 'string' && /@/.test(s);

  // Clicking the target jumps to /admin?tab=users&q=<email>. We use a full
  // SPA navigation via location so the parent Admin page picks up the new tab.
  const jumpToUser = (email) => {
    const url = new URL(window.location.origin + '/admin');
    url.searchParams.set('tab', 'users');
    url.searchParams.set('q', email);
    window.location.assign(url.toString());
  };

  const Chip = ({ id, label, count }) => (
    <button
      type="button"
      onClick={() => setActionChip((c) => (c === id ? '' : id))}
      data-testid={`audit-chip-${id || 'all'}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11.5px] font-bold uppercase tracking-[0.1em] border transition-colors ${
        actionChip === id
          ? 'bg-[#0e0e10] text-white border-[#0e0e10]'
          : 'bg-white text-[#1D1D1F] border-[#E5E5E7] hover:border-[#c9a227]'
      }`}
    >
      {label}{count != null && <span className={`text-[10px] ${actionChip === id ? 'text-white/70' : 'text-[#6E6E73]'}`}>· {count}</span>}
    </button>
  );

  const groupCount = (id) => rows.filter((r) => (ACTION_GROUPS[id] || []).includes(r.action)).length;

  return (
    <Section title={`Audit log (${filtered.length})`} icon={History}
      action={
        <button onClick={reload} aria-label="Refresh"
          className="text-[#6E6E73] hover:text-[#c9a227]"
          data-testid="academy-admin-audit-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      }>
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <Chip id="" label="All" count={rows.length} />
        <Chip id="access" label="Access" count={groupCount('access')} />
        <Chip id="password" label="Password" count={groupCount('password')} />
        <Chip id="enrollment" label="Users / Enrollment" count={groupCount('enrollment')} />
        <Chip id="lesson" label="Lessons" count={groupCount('lesson')} />
      </div>
      <input value={filter} onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter by action, target or actor email"
        data-testid="academy-admin-audit-filter"
        className="mb-3 px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13px] w-full max-w-[420px]" />
      {loading ? <div className="text-[#6E6E73] text-[13px]">Loading…</div> : filtered.length === 0 ? (
        <div className="text-[13.5px] text-[#6E6E73] py-3">No audit events yet — every admin action will land here.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[#FAFAFA] text-[#6E6E73]">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">When</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Actor</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Action</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Target</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E7]">
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                  <td className="px-3 py-2 text-[#6E6E73] whitespace-nowrap">{(r.ts || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-3 py-2 text-[#1D1D1F]">{r.actor_email || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border ${ACTION_BADGE[r.action] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                      {r.action}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[#6E6E73]">
                    {isEmailTarget(r.target) ? (
                      <button
                        type="button"
                        onClick={() => jumpToUser(r.target)}
                        data-testid={`audit-jump-${r.id}`}
                        title="Open in Users tab"
                        className="inline-flex items-center gap-1 text-[12px] font-mono text-[#1D1D1F] hover:text-[#c9a227] underline-offset-2 hover:underline"
                      >
                        <code className="text-[12px]">{r.target}</code>
                        <span className="text-[10px] text-[#c9a227]">→</span>
                      </button>
                    ) : (
                      <code className="text-[12px]">{r.target || '—'}</code>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[#6E6E73] text-[12px]">
                    {r.metadata && Object.keys(r.metadata).length > 0
                      ? Object.entries(r.metadata).map(([k, v]) => <span key={k} className="mr-2"><strong>{k}</strong>={String(v)}</span>)
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/* ======================================== BULK ENROLL ======================================== */

function BulkEnrollModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/api/academy/admin/enrollments/bulk', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Bulk enroll students (CSV)" onClose={onClose}>
      {!result ? (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-[13px] text-[#6E6E73]">
            Upload a CSV with columns: <code>email, name, program_slug, tier_id, schedule</code> (only <code>email</code> and <code>program_slug</code> are required).
            New users are auto-created and emailed temporary credentials. Already-enrolled students are skipped.
          </p>
          <input type="file" accept=".csv,text/csv" required
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="acad-bulk-file"
            className="block w-full text-[13px] file:mr-3 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-[#fef8e7] file:text-[#a8871a] file:font-bold file:cursor-pointer" />
          <a href={`data:text/csv;charset=utf-8,${encodeURIComponent('email,name,program_slug,tier_id,schedule\nstudent1@example.com,Alex Doe,programming-software-development,standard,Mon/Wed 6pm\n')}`}
            download="zhelta-academy-bulk-enroll-template.csv"
            className="inline-block text-[12px] text-[#c9a227] hover:underline font-semibold">
            ↓ Download CSV template
          </a>
          {error && <div className="text-red-600 text-[13px]">{error}</div>}
          <button type="submit" disabled={loading || !file}
            data-testid="acad-bulk-submit"
            className="w-full px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
            {loading ? 'Importing…' : 'Import CSV'}
          </button>
        </form>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Total" value={result.summary.total} />
            <Stat label="Enrolled" value={result.summary.enrolled} tone="emerald" />
            <Stat label="Created users" value={result.summary.users_created} tone="blue" />
            <Stat label="Skipped" value={result.summary.skipped} tone="red" />
          </div>
          <div className="max-h-[260px] overflow-y-auto rounded-lg border border-[#E5E5E7]">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#FAFAFA]">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase">Row</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase">Email</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase">Program</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E7]">
                {result.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-1.5 text-[#6E6E73]">{r.row}</td>
                    <td className="px-3 py-1.5">{r.email}</td>
                    <td className="px-3 py-1.5 text-[#6E6E73]"><code>{r.program_slug}</code></td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border
                        ${r.status === 'enrolled' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          r.status === 'skipped' ? 'bg-red-50 text-red-700 border-red-200' :
                          'bg-amber-50 text-amber-700 border-amber-200'}`}>
                        {r.status}
                      </span>
                      {r.reason && <span className="ml-2 text-[11px] text-[#6E6E73]">{r.reason}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={onDone} data-testid="acad-bulk-done"
            className="w-full px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10]"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>Done</button>
        </div>
      )}
    </ModalShell>
  );
}

function BulkM365Modal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/api/academy/admin/m365-access/bulk', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Bulk grant Microsoft 365 (CSV)" onClose={onClose}>
      {!result ? (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-[13px] text-[#6E6E73]">
            Upload a CSV with columns: <code>email, program_slug, m365_email</code> (required) plus optional <code>apps</code> (semi-colon separated, e.g. <code>outlook;word;teams</code>) and <code>notes</code>.
            When <code>apps</code> is omitted, the per-program template (with tier bonus) is auto-applied. The user must already exist and be enrolled in the listed program.
          </p>
          <input type="file" accept=".csv,text/csv" required
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="acad-bulk-m365-file"
            className="block w-full text-[13px] file:mr-3 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-[#fef8e7] file:text-[#a8871a] file:font-bold file:cursor-pointer" />
          <a href={`data:text/csv;charset=utf-8,${encodeURIComponent('email,program_slug,m365_email,apps,notes\nstudent1@example.com,programming-software-development,student1@yourtenant.onmicrosoft.com,,\nstudent2@example.com,creative-technology-media-literacy,student2@yourtenant.onmicrosoft.com,outlook;word;powerpoint;onedrive,Standard tier license\n')}`}
            download="zhelta-bulk-m365-template.csv"
            className="inline-block text-[12px] text-[#c9a227] hover:underline font-semibold">
            ↓ Download CSV template
          </a>
          {error && <div className="text-red-600 text-[13px]">{error}</div>}
          <button type="submit" disabled={loading || !file}
            data-testid="acad-bulk-m365-submit"
            className="w-full px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
            {loading ? 'Granting…' : 'Grant access'}
          </button>
        </form>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Total" value={result.summary.total} />
            <Stat label="Granted" value={result.summary.granted} tone="emerald" />
            <Stat label="Skipped" value={result.summary.skipped} tone="red" />
          </div>
          <div className="max-h-[260px] overflow-y-auto rounded-lg border border-[#E5E5E7]">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#FAFAFA]">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase">Row</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase">Email</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase">M365</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase">Apps</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E7]">
                {result.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-1.5 text-[#6E6E73]">{r.row}</td>
                    <td className="px-3 py-1.5">{r.email}</td>
                    <td className="px-3 py-1.5 text-[#6E6E73]"><code>{r.m365_email}</code></td>
                    <td className="px-3 py-1.5 text-[#6E6E73]">{(r.apps || []).join(', ')}</td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border
                        ${r.status === 'granted' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          r.status === 'skipped' ? 'bg-red-50 text-red-700 border-red-200' :
                          'bg-amber-50 text-amber-700 border-amber-200'}`}>
                        {r.status}
                      </span>
                      {r.reason && <span className="ml-2 text-[11px] text-[#6E6E73]">{r.reason}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={onDone} data-testid="acad-bulk-m365-done"
            className="w-full px-4 py-2.5 rounded-full text-[13px] font-bold uppercase tracking-wider text-[#0e0e10]"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>Done</button>
        </div>
      )}
    </ModalShell>
  );
}

function Stat({ label, value, tone = 'gold' }) {
  const tones = {
    gold:    'bg-amber-50 text-[#a8871a] border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue:    'bg-blue-50 text-blue-700 border-blue-200',
    red:     'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${tones[tone]}`}>
      <div className="text-[10px] uppercase font-bold tracking-wider opacity-80">{label}</div>
      <div className="text-[20px] font-extrabold leading-tight">{value}</div>
    </div>
  );
}

